/**
 * Sync modal: connect Google / Outlook, pick types + list filters, start sync.
 */
import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  RefreshCw,
  Unlink,
  type LucideIcon,
} from 'lucide-react';
import googleCalendarLogo from '@/assets/images/google-calendar.webp';
import outlookCalendarLogo from '@/assets/images/outlook-calendar.svg';
import {
  CALENDAR_LEGEND_ITEMS,
  EVENT_TYPE_CONFIG,
  SESSION_REMOTE_TYPE_CONFIG,
  SESSION_SYNC_GROUP_CONFIG,
  SESSION_TYPE_CONFIG,
  type CalendarLegendItem,
} from '@/components/calendar/calendar-configs';
import {
  applyCalendarSyncDraft,
  applyCalendarSyncFilterSeed,
  applyCalendarSyncHostLock,
  applyCalendarSyncPrefs,
  buildWizardSteps,
  isCalendarSyncAdminRole,
  isCalendarSyncJobInProgress,
  isCalendarSyncJobTerminal,
  CALENDAR_SYNC_REMINDER_PRESETS,
  type CalendarSyncReminderSessionType,
  type CalendarSyncSessionTypeGroup,
  type CalendarSyncWizardStep,
  clearCalendarSyncDraft,
  disconnectToastKey,
  emptyCalendarSyncState,
  patchSessionFilters,
  patchSessionRemoteFilters,
  readCalendarSyncDraft,
  reminderMinutesFor,
  reminderPresetLabel,
  isReminderSourceEnabled,
  isSessionGroupSelected,
  isSessionParticipateOnlyForTypes,
  resolveCalendarSyncParticipateOnly,
  sessionTypeGroupsForMode,
  setSessionParticipateOnlyForTypes,
  sessionTypesForAllowedTabs,
  sessionTypesForReminders,
  setSessionGroupEnabled,
  setReminderSourceEnabled,
  toCalendarSyncPrefsPayload,
  toggleReminderMinutes,
  toggleSyncType,
  toggleHolidayType,
  toggleRemoteSessionStatus,
  toggleSessionFilterValue,
  writeCalendarSyncDraft,
  setHolidayVacationZone,
  type CalendarSyncFilterSeed,
  type CalendarSyncState,
} from '@/utils/calendar-sync-utils';
import { resolveLucideIcon } from '@/hooks/useRoleConfig';
import { InfoModal } from '@/components/common/modals/InfoModal';
import {
  ALL_FILTER_GROUP_IDS as EVENT_FILTER_IDS,
  type EventFilters,
} from '@/components/events/event-configs';
import { EventFiltersContent } from '@/components/events/EventFilters';
import { Checkbox } from '@/components/forms/Checkbox';
import { Input } from '@/components/forms/Input';
import { RadioGroup, type RadioOption } from '@/components/forms/RadioGroup';
import { Select } from '@/components/forms/Select';
import {
  ALL_FILTER_GROUP_IDS as HOLIDAY_FILTER_IDS,
  type HolidayFilters,
} from '@/components/holidays/holiday-configs';
import { HolidayFiltersContent } from '@/components/holidays/HolidayFilters';
import { POST_IT_STATUS_CONFIG, THEMATICS } from '@/components/post-its/post-it-configs';
import {
  ALL_FILTER_GROUP_IDS as SESSION_FILTER_IDS,
  ALL_SESSION_REMOTE_FILTER_GROUP_IDS,
} from '@/components/sessions/session-configs';
import { SessionFilters as InPersonSessionFilters } from '@/components/sessions/inperson/SessionFilters';
import { SessionFilters as RemoteSessionFilters } from '@/components/sessions/remote/SessionFilters';
import { Button } from '@/components/forms/Button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { DATE_RANGE_PERIOD_OPTIONS } from '@/constants/shared-catalog';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { useMode } from '@/contexts/ModeContext';
import { cn } from '@/lib/utils';
import {
  disconnectCalendarProvider,
  getCalendarAuthUrl,
  getCalendarConnections,
  getCalendarSyncJob,
  pauseCalendarProvider,
  startCalendarSync,
} from '@/services/calendar-sync.service';
import type { CalendarEventType } from '@/types/calendar.types';
import type {
  CalendarConnection,
  CalendarSyncDisconnectMode,
  CalendarSyncJob,
  CalendarSyncProvider,
} from '@/types/calendar-sync.types';
import type { SessionTab as InPersonSessionTab } from '@/types/inperson/session.types';
import type { PostItFilters, PostItStatus, PostItThematic } from '@/types/post-it.types';
import type { SessionTab as RemoteSessionTab } from '@/types/remote/session.types';
import { toSessionInPersonTab, toSessionTab } from '@/utils/common-utils';
import { useAuthStore } from '@/stores/authStore';
import { getErrorMessage } from '@/utils/errors-utils';
import type { ManagedByValue } from '@/components/common/ManagedByMultiSelect';

/** Props for {@link CalendarSyncModal}. */
interface CalendarSyncModalProps {
  open: boolean;
  onClose: () => void;
  lockedTypes?: CalendarEventType[];
  filterSeed?: CalendarSyncFilterSeed;
}

/** Connected-chip row: provider mark, email, and the public web calendar URL. */
interface DestinationCard {
  provider: CalendarSyncProvider;
  label: string;
  logo: string;
  calendarUrl: string;
}

/** Google Agenda / Outlook Calendar destination cards. */
const DESTINATIONS: DestinationCard[] = [
  {
    provider: 'google',
    label: 'Google Agenda',
    logo: googleCalendarLogo,
    calendarUrl: 'https://calendar.google.com/',
  },
  {
    provider: 'outlook',
    label: 'Outlook Calendar',
    logo: outlookCalendarLogo,
    calendarUrl: 'https://outlook.office.com/calendar/',
  },
];

/** Product mark for a calendar provider card. */
function renderProviderLogo(card: DestinationCard, className: string): React.ReactNode {
  return <img src={card.logo} alt={card.label} className={className} />;
}

/** Visual Connecter mark inside the provider card (the card itself is the control). */
function renderProviderConnectMark(isConnecting: boolean): React.ReactNode {
  return (
    <span
      aria-hidden
      className="inline-flex items-center gap-2 rounded-md bg-[var(--color-2-nuance-1)] px-4 py-2 text-sm font-medium text-white shadow-md"
    >
      {isConnecting ? 'Redirection…' : 'Connecter'}
      {isConnecting ? null : <ChevronRight className="h-4 w-4" />}
    </span>
  );
}

/** In-person session tabs used only to resolve allowed type chips. */
const IN_PERSON_TAB_ORDER: InPersonSessionTab[] = ['tutoring', 'information-meeting'];

/** Remote session tabs used only to resolve allowed type chips. */
const REMOTE_TAB_ORDER: RemoteSessionTab[] = [
  'tutoring',
  'information-meeting',
  'welcome-meeting',
  'field-event',
];

/** Session list filters without the tab-scoped type group (chips live in the modal). */
const SESSION_LIST_FILTER_IDS = SESSION_FILTER_IDS.filter((id) => id !== 'filter-type');

/** Same icon as the sessions management tab (`SESSION_TYPE_CONFIG` / `SESSION_REMOTE_TYPE_CONFIG`). */
function sessionGroupIcon(
  group: CalendarSyncSessionTypeGroup,
  appMode: 'in_person' | 'remote',
): LucideIcon {
  const type = group.types[0]?.type;
  if (!type) {
    return Calendar;
  }
  if (appMode === 'remote') {
    return SESSION_REMOTE_TYPE_CONFIG[type]?.icon ?? SESSION_TYPE_CONFIG[type]?.icon ?? Calendar;
  }
  return SESSION_TYPE_CONFIG[type]?.icon ?? SESSION_REMOTE_TYPE_CONFIG[type]?.icon ?? Calendar;
}

/** Keep vs wipe cards for the disconnect confirm dialog. */
const DISCONNECT_OPTIONS: RadioOption[] = [
  {
    value: 'keep',
    label: 'Garder le passé',
    description: 'Laisse les événements déjà exportés et arrête les mises à jour.',
  },
  {
    value: 'wipe',
    label: 'Tout supprimer',
    description: 'Retire le calendrier ZUPdeCO de votre agenda externe.',
  },
];

/** Factual note that a first sync or later upcoming-item writes can take a moment. */
const SYNC_DURATION_HINT =
  'La première synchronisation, ainsi que les créations, mises à jour ou suppressions d’événements à venir, peuvent prendre quelques instants selon le volume.';

/** Extra line when the export job reports `slow`. */
const SYNC_SLOW_HINT = 'Cela peut prendre un moment.';

/** `--filter-color` for reminder presets (same brand token as filter-badge chips). */
const REMINDER_PRESET_FILTER_COLOR = 'var(--color-2-nuance-1)';

/** Email and connection status on the compact destination chip. */
function destinationStatusText(connection: CalendarConnection): string {
  const email = connection.externalEmail ?? '';
  if (connection.status === 'paused') {
    return `${email} — ${getErrorMessage('calendarSyncPaused')}`;
  }
  if (connection.status === 'error') {
    return `${email} — ${getErrorMessage('calendarReconnectRequired')}`;
  }
  return email;
}

/** Icon tone for the progress-step calendar mark. */
function progressCalendarToneClass(isComplete: boolean, isFailed: boolean): string {
  if (isComplete) {
    return 'text-success';
  }
  if (isFailed) {
    return 'text-destructive';
  }
  return 'text-primary animate-pulse';
}

/** Completed sync message and close action. */
function renderProgressComplete(processedItems: number, onClose: () => void): React.ReactNode {
  return (
    <>
      <p className="text-lg font-semibold text-center">Synchronisation terminée</p>
      <p className="text-sm text-muted-foreground text-center">
        {processedItems} éléments synchronisés
      </p>
      <Button variant="primary" onClick={onClose}>
        Fermer
      </Button>
    </>
  );
}

/** Failed sync message and close action. */
function renderProgressFailed(error: string | null, onClose: () => void): React.ReactNode {
  return (
    <>
      <p className="text-lg font-semibold text-destructive text-center">
        Échec de la synchronisation
      </p>
      <p className="text-sm text-muted-foreground text-center">{error ?? 'Erreur inconnue'}</p>
      <Button variant="primary" onClick={onClose}>
        Fermer
      </Button>
    </>
  );
}

/** In-flight sync progress bar and optional slow hint. */
function renderProgressRunning(job: CalendarSyncJob, progress: number): React.ReactNode {
  return (
    <>
      <p className="text-lg font-semibold text-center">
        {job.processedItems} / {job.totalItems || '...'} éléments synchronisés
      </p>
      <div className="w-full max-w-md">
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      {job.status === 'slow' ? (
        <div className="rounded-md border border-yellow-500/20 bg-yellow-500/10 px-4 py-3 max-w-md">
          <p className="text-sm text-yellow-700 dark:text-yellow-400 text-center">
            La première synchronisation peut prendre quelques minutes selon le volume.
          </p>
        </div>
      ) : null}
    </>
  );
}

/** Body under the calendar icon once a sync job exists. */
function renderProgressStatus(
  job: CalendarSyncJob,
  progress: number,
  onClose: () => void,
): React.ReactNode {
  if (job.status === 'completed') {
    return renderProgressComplete(job.processedItems, onClose);
  }
  if (job.status === 'failed') {
    return renderProgressFailed(job.error, onClose);
  }
  return renderProgressRunning(job, progress);
}

/** Sync modal: destination, types, list filters, job progress. */
export const CalendarSyncModal: React.FC<Readonly<CalendarSyncModalProps>> = ({
  open,
  onClose,
  lockedTypes,
  filterSeed,
}) => {
  const { mode } = useMode();
  const queryClient = useQueryClient();
  const appMode = mode === 'remote' ? 'remote' : 'in_person';
  const { roles } = useAuthStore();
  const { elements, isLoading: elementsLoading } = useInterfaceElements('sessions');
  const showParticipateOnly = !isCalendarSyncAdminRole(roles);
  const [form, setForm] = React.useState<CalendarSyncState>(() =>
    emptyCalendarSyncState('tutoring', lockedTypes),
  );
  const [currentStep, setCurrentStep] = React.useState<CalendarSyncWizardStep>('provider');
  const [job, setJob] = React.useState<CalendarSyncJob | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);
  /** Provider being connected — blocks both cards while OAuth redirect is in flight. */
  const [connectingProvider, setConnectingProvider] = React.useState<CalendarSyncProvider | null>(
    null,
  );
  const [disconnectOpen, setDisconnectOpen] = React.useState(false);
  const [disconnectMode, setDisconnectMode] = React.useState<CalendarSyncDisconnectMode>('keep');
  /** Which session-type group accordions are open (group id → boolean). */
  const [expandedSessionGroups, setExpandedSessionGroups] = React.useState<Record<string, boolean>>(
    {},
  );
  /** Which edit-mode source cards the user opened (type → open). */
  const [expandedSources, setExpandedSources] = React.useState<
    Partial<Record<CalendarEventType, boolean>>
  >({});
  const [eventExpanded, setEventExpanded] = React.useState(false);
  const [postitExpanded, setPostitExpanded] = React.useState(false);
  const [holidayExpanded, setHolidayExpanded] = React.useState(false);
  const hydratedOpen = React.useRef(false);
  const seedAppliedRef = React.useRef(false);
  const filterSeedRef = React.useRef(filterSeed);
  filterSeedRef.current = filterSeed;

  const {
    data: connectionsData,
    isPending: connectionsPending,
    refetch: refetchConnections,
  } = useQuery({
    queryKey: ['calendar-sync-connections'],
    queryFn: getCalendarConnections,
    enabled: open,
  });
  const connections = connectionsData?.connections ?? [];
  const waitingForConnections = open && connectionsPending && !connectionsData;
  const hadActiveConnectionRef = React.useRef(false);

  const selectedConnection = connections.find((row) => row.provider === form.provider);
  const hasActiveConnection = connections.some(
    (conn) => conn.status === 'active' || conn.status === 'paused',
  );
  const isWizardMode = !hasActiveConnection && !lockedTypes;
  const wizardSteps = React.useMemo(
    () => buildWizardSteps(form.enabledTypes, appMode),
    [form.enabledTypes, appMode],
  );

  React.useEffect(() => {
    if (open) {
      return;
    }
    hydratedOpen.current = false;
    seedAppliedRef.current = false;
    hadActiveConnectionRef.current = false;
    setCurrentStep('provider');
    setExpandedSessionGroups({});
    setExpandedSources({});
    setEventExpanded(false);
    setPostitExpanded(false);
    setHolidayExpanded(false);
  }, [open]);

  React.useEffect(() => {
    if (!open || hydratedOpen.current) {
      return;
    }
    const seed = filterSeedRef.current;
    const rows = connectionsData?.connections ?? [];
    const draft = readCalendarSyncDraft();
    if (draft) {
      setForm((current) =>
        applyCalendarSyncHostLock(
          applyCalendarSyncDraft(emptyCalendarSyncState(current.sessionTab, lockedTypes), draft),
          lockedTypes,
        ),
      );
      if (draft.wizardStep) {
        setCurrentStep(draft.wizardStep);
      }
      hydratedOpen.current = true;
      return;
    }
    if (!connectionsData) {
      if (!seedAppliedRef.current) {
        seedAppliedRef.current = true;
        setForm((current) =>
          applyCalendarSyncHostLock(
            applyCalendarSyncFilterSeed(
              emptyCalendarSyncState(current.sessionTab, lockedTypes),
              seed,
            ),
            lockedTypes,
          ),
        );
      }
      return;
    }
    const withPrefs = rows.find((row) => row.prefs);
    const fallback = withPrefs ?? rows[0];
    const savedPrefs = fallback?.prefs;
    setForm((current) => {
      let next = emptyCalendarSyncState(current.sessionTab, lockedTypes);
      const provider = current.provider ?? fallback?.provider ?? null;
      if (fallback && savedPrefs) {
        return applyCalendarSyncHostLock(
          applyCalendarSyncPrefs(next, provider ?? fallback.provider, savedPrefs),
          lockedTypes,
        );
      }
      if (provider) {
        next = { ...next, provider };
      }
      return applyCalendarSyncHostLock(applyCalendarSyncFilterSeed(next, seed), lockedTypes);
    });
    hydratedOpen.current = true;
  }, [open, connectionsData, lockedTypes]);

  React.useEffect(() => {
    if (!open || waitingForConnections) {
      return;
    }
    if (!hasActiveConnection && hadActiveConnectionRef.current) {
      resetToWizardStart();
    }
    hadActiveConnectionRef.current = hasActiveConnection;
  }, [open, hasActiveConnection, waitingForConnections]);

  React.useEffect(() => {
    if (currentStep === 'events') {
      setEventExpanded(true);
    }
    if (currentStep === 'postits') {
      setPostitExpanded(true);
    }
    if (currentStep === 'holidays') {
      setHolidayExpanded(true);
    }
  }, [currentStep]);

  const allowedSessionTabs = React.useMemo(() => {
    if (elementsLoading) {
      return appMode === 'remote' ? REMOTE_TAB_ORDER : IN_PERSON_TAB_ORDER;
    }
    const tabElements = elements.filter((el) => el.elementType === 'tab');
    if (tabElements.length === 0) {
      return appMode === 'remote' ? REMOTE_TAB_ORDER : IN_PERSON_TAB_ORDER;
    }
    if (appMode === 'remote') {
      return tabElements
        .map((el) => toSessionTab(el.elementId))
        .filter((id): id is RemoteSessionTab => id !== null && id !== 'all')
        .sort((a, b) => REMOTE_TAB_ORDER.indexOf(a) - REMOTE_TAB_ORDER.indexOf(b));
    }
    return tabElements
      .map((el) => toSessionInPersonTab(el.elementId))
      .filter((id): id is InPersonSessionTab => id !== null)
      .sort((a, b) => IN_PERSON_TAB_ORDER.indexOf(a) - IN_PERSON_TAB_ORDER.indexOf(b));
  }, [appMode, elements, elementsLoading]);

  const sessionTypeOptions = React.useMemo(
    () => sessionTypesForAllowedTabs(appMode, allowedSessionTabs),
    [allowedSessionTabs, appMode],
  );

  const selectedSessionTypes =
    appMode === 'remote' ? form.sessionRemoteFilters.types : form.sessionFilters.types;

  const reminderSessionTypes = sessionTypesForReminders(appMode, selectedSessionTypes).filter(
    (row) => sessionTypeOptions.some((option) => option.type === row.type),
  );

  const reminderSessionGroups = sessionTypeGroupsForMode(
    appMode,
    reminderSessionTypes.map((item) => item.type),
  );

  React.useEffect(() => {
    if (!job || isCalendarSyncJobTerminal(job.status)) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      void pollJob(job.id);
    }, 1500);
    return () => window.clearInterval(timer);
  }, [job]);

  async function pollJob(jobId: string): Promise<void> {
    try {
      const next = await getCalendarSyncJob(jobId);
      if (next.status === 'slow' && job?.status !== 'slow') {
        toast.warning(getErrorMessage('calendarSyncSlow'));
      }
      setJob(next);
      if (next.status === 'completed') {
        if (next.totalItems === 0) {
          toast.warning(getErrorMessage('calendarSyncEmpty'));
        } else {
          toast.success(getErrorMessage('calendarSyncDone'));
        }
        void refetchConnections();
      }
      if (next.status === 'failed') {
        toast.error(getErrorMessage(next.error ?? 'calendarSyncFailed'));
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }

  async function connectProvider(provider: CalendarSyncProvider): Promise<void> {
    setConnectingProvider(provider);
    try {
      const next = { ...form, provider };
      setForm(next);
      writeCalendarSyncDraft(next, 'sources');
      const { url } = await getCalendarAuthUrl(provider, 'staff');
      window.location.assign(url);
    } catch (error) {
      toast.error(getErrorMessage(error));
      setConnectingProvider(null);
    }
  }

  function handleOpenDisconnect(): void {
    setDisconnectMode('keep');
    setDisconnectOpen(true);
  }

  function handleCloseDisconnect(): void {
    setDisconnectOpen(false);
  }

  function handleDisconnectModeChange(value: string): void {
    if (value === 'keep' || value === 'wipe') {
      setDisconnectMode(value);
    }
  }

  /** Back to the provider step after the last connection is gone. */
  function resetToWizardStart(): void {
    clearCalendarSyncDraft();
    setCurrentStep('provider');
    setConnectingProvider(null);
    setExpandedSessionGroups({});
    setExpandedSources({});
    setEventExpanded(false);
    setPostitExpanded(false);
    setHolidayExpanded(false);
    setForm((current) => emptyCalendarSyncState(current.sessionTab, lockedTypes));
  }

  async function handleWipeDisconnect(): Promise<void> {
    if (!form.provider) {
      return;
    }
    setDisconnecting(true);
    try {
      await disconnectCalendarProvider(form.provider);
      queryClient.setQueryData(['calendar-sync-connections'], { connections: [] });
      resetToWizardStart();
      toast.success(getErrorMessage(disconnectToastKey('wipe')));
      setDisconnectOpen(false);
      void refetchConnections();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDisconnecting(false);
    }
  }

  async function handleKeepDisconnect(): Promise<void> {
    if (!form.provider) {
      return;
    }
    setDisconnecting(true);
    try {
      await pauseCalendarProvider(form.provider);
      toast.success(getErrorMessage(disconnectToastKey('keep')));
      setDisconnectOpen(false);
      void refetchConnections();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDisconnecting(false);
    }
  }

  function handleConfirmDisconnect(): void {
    if (disconnectMode === 'wipe') {
      void handleWipeDisconnect();
      return;
    }
    void handleKeepDisconnect();
  }

  async function handleSync(): Promise<void> {
    if (!form.provider) {
      toast.error(getErrorMessage('calendarProviderRequired'));
      return;
    }
    if (form.enabledTypes.length === 0) {
      toast.error(getErrorMessage('calendarTypesRequired'));
      return;
    }
    if (
      !selectedConnection ||
      (selectedConnection.status !== 'active' && selectedConnection.status !== 'paused')
    ) {
      toast.error(getErrorMessage('calendarReconnectRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const prefs = toCalendarSyncPrefsPayload(
        showParticipateOnly ? form : { ...form, participateOnly: null },
        appMode,
      );
      const started = await startCalendarSync({
        provider: form.provider,
        kind: selectedConnection.lastSyncedAt ? 'reconcile' : 'initial',
        prefs,
      });
      setJob(started);
      clearCalendarSyncDraft();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  }

  /** Jump to the progress step and start the export job. */
  function handleConfirmAndSync(): void {
    setCurrentStep('progress');
    void handleSync();
  }

  /** Toggle a source type on the form when the checkbox no longer matches prefs. */
  function alignEnabledType(type: CalendarEventType, enabled: boolean): void {
    if (enabled !== form.enabledTypes.includes(type)) {
      setForm((current) => toggleSyncType(current, type));
    }
  }

  /** Include or exclude events from the wizard sync. */
  function handleEventToggle(): void {
    const enabled = !eventExpanded;
    setEventExpanded(enabled);
    alignEnabledType('event', enabled);
  }

  /** Include or exclude post-its from the wizard sync. */
  function handlePostitToggle(): void {
    const enabled = !postitExpanded;
    setPostitExpanded(enabled);
    alignEnabledType('postit', enabled);
  }

  /** Include or exclude holidays from the wizard sync. */
  function handleHolidayToggle(): void {
    const enabled = !holidayExpanded;
    setHolidayExpanded(enabled);
    alignEnabledType('holiday', enabled);
  }

  function handleClose(): void {
    onClose();
  }

  /** Move to the next wizard step. */
  function goToNextStep(): void {
    const currentIndex = wizardSteps.indexOf(currentStep);
    if (currentIndex >= 0 && currentIndex < wizardSteps.length - 1) {
      setCurrentStep(wizardSteps[currentIndex + 1]!);
    }
  }

  /** Move to the previous wizard step. */
  function goToPreviousStep(): void {
    const currentIndex = wizardSteps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(wizardSteps[currentIndex - 1]!);
    }
  }

  /** Provider selection step (2 large cards side by side). */
  function renderStepProvider(): React.ReactNode {
    function renderProviderCard(card: DestinationCard): React.ReactNode {
      const connection = connections.find((row) => row.provider === card.provider);
      if (
        hasActiveConnection &&
        connection?.status !== 'active' &&
        connection?.status !== 'paused'
      ) {
        return null;
      }

      const isThisConnecting = connectingProvider === card.provider;
      const isOtherConnecting = connectingProvider !== null && connectingProvider !== card.provider;

      function handleSelectProvider(): void {
        if (connectingProvider !== null) {
          return;
        }
        void connectProvider(card.provider);
      }

      return (
        <button
          key={card.provider}
          type="button"
          disabled={connectingProvider !== null}
          aria-label={card.label}
          className={[
            'flex min-h-[240px] flex-col items-center justify-center rounded-xl border-2 px-8 py-10 transition-all',
            isOtherConnecting
              ? 'border-border opacity-40 cursor-not-allowed'
              : 'border-border hover:border-primary hover:bg-muted/40 cursor-pointer',
          ].join(' ')}
          onClick={handleSelectProvider}
        >
          <div className="mb-5">{renderProviderLogo(card, 'h-20 w-20')}</div>
          <span className="mb-4 text-xl font-semibold">{card.label}</span>
          {renderProviderConnectMark(isThisConnecting)}
        </button>
      );
    }

    return (
      <div className="space-y-4">
        <h3 className="text-center text-lg font-semibold">Choisissez votre calendrier</h3>
        <div className="grid gap-6 md:grid-cols-2">{DESTINATIONS.map(renderProviderCard)}</div>
      </div>
    );
  }

  /** Sources selection step (multi-select checkboxes). */
  function renderStepSources(): React.ReactNode {
    const canProceed = form.enabledTypes.length > 0;

    return (
      <div className="space-y-4">
        <h3 className="text-center text-lg font-semibold">Que souhaitez-vous synchroniser ?</h3>
        <div className="space-y-2">{CALENDAR_LEGEND_ITEMS.map(renderTypeCard)}</div>
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={goToPreviousStep}
            icon={ChevronLeft}
            iconPosition="left"
          >
            Retour
          </Button>
          <Button
            variant="primary"
            onClick={goToNextStep}
            disabled={!canProceed}
            icon={ChevronRight}
            iconPosition="right"
          >
            Suivant
          </Button>
        </div>
      </div>
    );
  }

  /** Sessions step: one accordion per session-type group (Tutorat, RI, RA…). */
  /** Open only after the user opens this kind — sessions page stays collapsed. */
  function isSessionGroupExpanded(groupId: string): boolean {
    return expandedSessionGroups[groupId] === true;
  }

  /** First-level accordion for one session kind (Tutorat, RI…) — no parent Séances card. */
  function renderGroupAccordion(group: CalendarSyncSessionTypeGroup): React.ReactNode {
    if (form.lockedTypes?.length && !form.lockedTypes.includes('session')) {
      return null;
    }
    const groupTypeCodes = group.types.map((item) => item.type);
    const isEnabled =
      form.enabledTypes.includes('session') &&
      isSessionGroupSelected(selectedSessionTypes, groupTypeCodes);
    const expanded = isSessionGroupExpanded(group.id);
    const GroupIcon = sessionGroupIcon(group, appMode);
    const groupConfig = SESSION_SYNC_GROUP_CONFIG[group.id] ?? SESSION_SYNC_GROUP_CONFIG.tutoring;

    function handleGroupExpand(): void {
      const nextOpen = !isSessionGroupExpanded(group.id);
      setExpandedSessionGroups((prev) => ({ ...prev, [group.id]: nextOpen }));
      if (nextOpen && !isEnabled) {
        setForm((current) => setSessionGroupEnabled(current, appMode, groupTypeCodes, true));
      }
    }

    function handleGroupCheck(): void {
      setForm((current) => setSessionGroupEnabled(current, appMode, groupTypeCodes, !isEnabled));
    }

    return (
      <div
        key={group.id}
        className={[
          'rounded-2xl overflow-hidden transition-all duration-200',
          isEnabled
            ? 'ring-2 ring-primary shadow-md'
            : 'ring-1 ring-border shadow-sm hover:ring-primary/50 hover:shadow-md',
        ].join(' ')}
      >
        <div
          className={[
            'flex w-full items-center gap-3 px-5 py-4 transition-colors',
            expanded ? 'bg-primary/10' : 'bg-card hover:bg-primary/5',
          ].join(' ')}
        >
          <button
            type="button"
            onClick={handleGroupExpand}
            aria-expanded={expanded}
            className="flex min-w-0 flex-1 items-center justify-between text-left"
          >
            <div className="flex items-center gap-4">
              <div className={['p-2.5 rounded-xl', groupConfig.bgClass].join(' ')}>
                <GroupIcon className="h-5 w-5" style={{ color: groupConfig.color }} />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-base font-bold text-foreground">{group.label}</span>
                <span className="text-xs text-muted-foreground">
                  {isEnabled ? 'Inclus dans la synchronisation' : 'Exclu de la synchronisation'}
                </span>
              </div>
            </div>
            <ChevronDown
              className={[
                'h-5 w-5 text-primary transition-transform duration-200',
                expanded ? 'rotate-180' : '',
              ].join(' ')}
            />
          </button>
          <Checkbox checked={isEnabled} onCheckedChange={handleGroupCheck} label="" />
        </div>
        {expanded ? (
          <div className="bg-background px-5 pb-6 pt-5 space-y-5 border-t-2 border-primary/20">
            {renderParticipateOnlyFilter(groupTypeCodes)}
            {renderSessionListFilters()}
            {renderSourceReminders('session', group.id)}
          </div>
        ) : null}
      </div>
    );
  }

  /** Sessions wizard step. */
  function renderStepSessions(): React.ReactNode {
    const groups = sessionTypeGroupsForMode(appMode);

    return (
      <div className="space-y-4">
        <div className="space-y-1">
          <h3 className="text-center text-lg font-semibold">Configurer les séances</h3>
          <p className="text-center text-sm text-muted-foreground">
            Cochez les catégories à inclure. Décochez pour les exclure du calendrier.
          </p>
        </div>
        <div className="space-y-3">{groups.map(renderGroupAccordion)}</div>
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={goToPreviousStep}
            icon={ChevronLeft}
            iconPosition="left"
          >
            Retour
          </Button>
          <Button
            variant="primary"
            onClick={goToNextStep}
            disabled={selectedSessionTypes.length === 0}
            icon={ChevronRight}
            iconPosition="right"
          >
            Suivant
          </Button>
        </div>
      </div>
    );
  }

  /** Events step (in-person only). */
  function renderStepEvents(): React.ReactNode {
    return (
      <div className="space-y-4">
        <h3 className="text-center text-lg font-semibold">Configurer les événements</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox
              label="Inclure les événements dans la synchronisation"
              checked={eventExpanded}
              onCheckedChange={handleEventToggle}
            />
          </div>
          {eventExpanded ? (
            <div className="space-y-4 pl-6">
              {renderParticipateOnlyFilter()}
              <EventFiltersContent
                filters={form.eventFilters}
                allowedFilterIds={EVENT_FILTER_IDS}
                visibleFilterIds={EVENT_FILTER_IDS}
                onFiltersChange={handleEventFiltersChange}
              />
              {renderSourceReminders('event')}
            </div>
          ) : null}
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={goToPreviousStep}
            icon={ChevronLeft}
            iconPosition="left"
          >
            Retour
          </Button>
          <Button variant="primary" onClick={goToNextStep} icon={ChevronRight} iconPosition="right">
            Suivant
          </Button>
        </div>
      </div>
    );
  }

  /** Post-its step. */
  function renderStepPostits(): React.ReactNode {
    return (
      <div className="space-y-4">
        <h3 className="text-center text-lg font-semibold">Configurer les post-its</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox
              label="Inclure les post-its dans la synchronisation"
              checked={postitExpanded}
              onCheckedChange={handlePostitToggle}
            />
          </div>
          {postitExpanded ? (
            <div className="space-y-4 pl-6">
              <div className="grid gap-3 md:grid-cols-2">
                <Select
                  label="Thématique"
                  value={form.postitFilters.thematic ?? ''}
                  onChange={handlePostitThematic}
                  options={thematicOptions}
                />
                <Select
                  label="Statut"
                  value={form.postitFilters.status ?? ''}
                  onChange={handlePostitStatus}
                  options={statusOptions}
                />
                <Select
                  label="Période"
                  value={form.postitFilters.dateRange ?? 'all'}
                  onChange={handlePostitDateRange}
                  options={[...DATE_RANGE_PERIOD_OPTIONS]}
                />
                <Input
                  label="Recherche"
                  value={form.postitFilters.search ?? ''}
                  onChange={handlePostitSearch}
                />
                <Input
                  label="Date de début"
                  type="date"
                  value={form.postitFilters.dateFrom ?? ''}
                  onChange={handlePostitDateFrom}
                />
                <Input
                  label="Date de fin"
                  type="date"
                  value={form.postitFilters.dateTo ?? ''}
                  onChange={handlePostitDateTo}
                />
                <Checkbox
                  label="Prioritaires uniquement"
                  checked={form.postitFilters.priority === true}
                  onCheckedChange={handlePostitPriority}
                />
              </div>
              {renderSourceReminders('postit')}
            </div>
          ) : null}
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={goToPreviousStep}
            icon={ChevronLeft}
            iconPosition="left"
          >
            Retour
          </Button>
          <Button variant="primary" onClick={goToNextStep} icon={ChevronRight} iconPosition="right">
            Suivant
          </Button>
        </div>
      </div>
    );
  }

  /** Holidays step. */
  function renderStepHolidays(): React.ReactNode {
    return (
      <div className="space-y-4">
        <h3 className="text-center text-lg font-semibold">Configurer les vacances</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox
              label="Inclure les vacances dans la synchronisation"
              checked={holidayExpanded}
              onCheckedChange={handleHolidayToggle}
            />
          </div>
          {holidayExpanded ? (
            <div className="space-y-4 pl-6">
              <HolidayFiltersContent
                filters={form.holidayFilters}
                allowedFilterIds={HOLIDAY_FILTER_IDS}
                visibleFilterIds={HOLIDAY_FILTER_IDS}
                onToggleType={handleHolidayTypeToggle}
                onSetDateRange={handleHolidayDateRange}
                onSetCustomDateRange={handleHolidayCustomDates}
                onSetVacationZone={handleHolidayVacationZone}
              />
              {renderSourceReminders('holiday')}
            </div>
          ) : null}
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={goToPreviousStep}
            icon={ChevronLeft}
            iconPosition="left"
          >
            Retour
          </Button>
          <Button variant="primary" onClick={goToNextStep} icon={ChevronRight} iconPosition="right">
            Suivant
          </Button>
        </div>
      </div>
    );
  }

  /** Final confirmation step with summary. */
  function renderStepConfirm(): React.ReactNode {
    const activeTypes = form.enabledTypes
      .map((type) => EVENT_TYPE_CONFIG[type]?.label ?? type)
      .join(', ');

    return (
      <div className="space-y-6">
        <h3 className="text-center text-lg font-semibold">Récapitulatif</h3>
        <div className="rounded-lg border border-border bg-muted/40 p-4">
          <p className="text-center text-sm">
            Vous allez synchroniser : <span className="font-semibold">{activeTypes}</span>
          </p>
          {form.provider ? (
            <p className="text-center text-sm text-muted-foreground mt-2">
              Destination : {form.provider === 'google' ? 'Google Agenda' : 'Outlook Calendar'}
            </p>
          ) : null}
        </div>
        {renderSyncHints()}
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={goToPreviousStep}
            icon={ChevronLeft}
            iconPosition="left"
          >
            Retour
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmAndSync}
            loading={submitting}
            icon={Check}
            iconPosition="right"
          >
            Confirmer et synchroniser
          </Button>
        </div>
      </div>
    );
  }

  /** Centered full-modal progress loader. */
  function renderStepProgress(): React.ReactNode {
    if (!job) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <Calendar className="w-16 h-16 text-muted-foreground animate-pulse mb-4" />
          <p className="text-lg font-semibold">Préparation de la synchronisation...</p>
        </div>
      );
    }

    const progress = job.totalItems > 0 ? (job.processedItems / job.totalItems) * 100 : 0;
    const isComplete = job.status === 'completed';
    const isFailed = job.status === 'failed';

    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-6">
        <Calendar className={cn('w-16 h-16', progressCalendarToneClass(isComplete, isFailed))} />
        {renderProgressStatus(job, progress, handleClose)}
      </div>
    );
  }

  /** Centered spinner while GET /calendar-sync/connections is in flight. */
  function renderConnectionsLoader(): React.ReactNode {
    return (
      <div className="absolute inset-0 grid place-items-center">
        <Spinner size="lg" label="Chargement…" />
      </div>
    );
  }

  /** Wizard, edit form, or connections loader. */
  function renderModalBody(): React.ReactNode {
    if (waitingForConnections) {
      return renderConnectionsLoader();
    }
    if (isWizardMode || currentStep === 'progress') {
      return renderWizardContent();
    }
    return (
      <div className="space-y-3">
        <div className="flex items-stretch gap-2">
          {DESTINATIONS.map(renderDestination)}
          {renderSyncHints()}
        </div>
        {renderSessionSection()}
        {renderEventSection()}
        {renderPostitSection()}
        {renderHolidaySection()}
        {renderJobProgress()}
      </div>
    );
  }

  /** Render the current wizard step content. */
  function renderWizardContent(): React.ReactNode {
    switch (currentStep) {
      case 'provider':
        return renderStepProvider();
      case 'sources':
        return renderStepSources();
      case 'sessions':
        return renderStepSessions();
      case 'events':
        return renderStepEvents();
      case 'postits':
        return renderStepPostits();
      case 'holidays':
        return renderStepHolidays();
      case 'confirm':
        return renderStepConfirm();
      case 'progress':
        return renderStepProgress();
      default:
        return null;
    }
  }

  /** Connected provider chip: opens Google Agenda or Outlook in a new tab. */
  function renderDestination(card: DestinationCard): React.ReactNode {
    const connection = connections.find((row) => row.provider === card.provider);
    const selected = form.provider === card.provider;

    // Hide non-connected provider when one is already active
    if (
      hasActiveConnection &&
      (!connection || (connection.status !== 'active' && connection.status !== 'paused'))
    ) {
      return null;
    }

    const isThisConnecting = connectingProvider === card.provider;

    function handleConnect(): void {
      void connectProvider(card.provider);
    }

    return (
      <div
        key={card.provider}
        className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 ${
          selected ? 'border-primary' : 'border-border'
        }`}
      >
        <a
          href={card.calendarUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-left text-sm"
        >
          {renderProviderLogo(card, 'h-6 w-6 shrink-0')}
          <span className="font-medium">{card.label}</span>
          {connection ? (
            <span className="text-[var(--color-2-nuance-1)]">
              {destinationStatusText(connection)}
            </span>
          ) : null}
        </a>
        {connection && (connection.status === 'error' || connection.status === 'revoked') ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleConnect}
            loading={isThisConnecting}
            disabled={connectingProvider !== null}
          >
            {isThisConnecting ? 'Redirection…' : 'Reconnecter'}
          </Button>
        ) : null}
        {connection ? null : (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleConnect}
            loading={isThisConnecting}
            disabled={connectingProvider !== null}
          >
            {isThisConnecting ? 'Redirection…' : 'Connecter'}
          </Button>
        )}
        {connection && (connection.status === 'active' || connection.status === 'paused') ? (
          <Button
            variant="ghost"
            size="sm"
            icon={Unlink}
            color="rgb(var(--color-error))"
            tooltipText="Déconnecter l’agenda"
            onClick={handleOpenDisconnect}
            className="h-auto shrink-0 bg-transparent px-2 text-xs underline shadow-none enabled:hover:bg-[color-mix(in_srgb,rgb(var(--color-error))_18%,transparent)]"
          >
            Déconnecter
          </Button>
        ) : null}
      </div>
    );
  }

  /** Participate-only for a session-kind group, or for events when no types are passed. */
  function renderParticipateOnlyFilter(typeCodes?: readonly string[]): React.ReactNode {
    if (!showParticipateOnly) {
      return null;
    }
    const checked = typeCodes?.length
      ? isSessionParticipateOnlyForTypes(form, typeCodes)
      : resolveCalendarSyncParticipateOnly(form.participateOnly);

    function handleParticipateOnly(next: boolean): void {
      if (typeCodes?.length) {
        setForm((current) => setSessionParticipateOnlyForTypes(current, typeCodes, next));
        return;
      }
      setForm((current) => ({ ...current, participateOnly: next }));
    }

    return (
      <section>
        <Checkbox
          label="Uniquement les événements auxquels je participe"
          checked={checked}
          onCheckedChange={handleParticipateOnly}
        />
      </section>
    );
  }

  /** Duration hint in color-2, beside the destination chip. */
  function renderSyncHints(): React.ReactNode {
    return (
      <p className="flex min-w-0 flex-1 items-center gap-1.5 rounded-lg border border-[var(--color-2-nuance-1)] px-2.5 py-1.5 text-xs font-medium text-[var(--color-2-nuance-1)]">
        <CircleHelp className="h-4 w-4 shrink-0" aria-hidden />
        <span>{SYNC_DURATION_HINT}</span>
      </p>
    );
  }

  function handleRemindersEnabled(sourceType: CalendarEventType, checked: boolean): void {
    setForm((current) => ({
      ...current,
      reminders: setReminderSourceEnabled(current.reminders, sourceType, checked),
    }));
  }

  function renderReminderPreset(
    sourceType: CalendarEventType,
    minutes: number,
    sessionType?: string,
  ): React.ReactNode {
    const selected = reminderMinutesFor(form.reminders, sourceType, sessionType).includes(minutes);

    function handleToggle(): void {
      setForm((current) => ({
        ...current,
        reminders: toggleReminderMinutes(
          current.reminders,
          sourceType,
          minutes,
          sessionType,
          form.provider,
        ),
      }));
    }

    return (
      <Button
        key={`${sourceType}-${sessionType ?? 'all'}-${minutes}`}
        type="button"
        variant="custom"
        size="sm"
        className={cn(
          'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
          selected ? 'filter-badge-active' : 'filter-badge-inactive',
        )}
        style={{ '--filter-color': REMINDER_PRESET_FILTER_COLOR } as React.CSSProperties}
        onClick={handleToggle}
      >
        {reminderPresetLabel(minutes)}
      </Button>
    );
  }

  function renderSessionTypeReminderRow(item: CalendarSyncReminderSessionType): React.ReactNode {
    function renderSessionPreset(minutes: number): React.ReactNode {
      return renderReminderPreset('session', minutes, item.type);
    }

    return (
      <div key={item.type} className="space-y-2">
        <p className="text-sm font-medium">{item.label}</p>
        <div className="flex flex-wrap gap-2">
          {CALENDAR_SYNC_REMINDER_PRESETS.map(renderSessionPreset)}
        </div>
      </div>
    );
  }

  function renderSessionReminderGroup(group: CalendarSyncSessionTypeGroup): React.ReactNode {
    return (
      <div key={group.id} className="space-y-3">
        <p className="text-sm font-semibold">{group.label}</p>
        {group.types.map(renderSessionTypeReminderRow)}
      </div>
    );
  }

  function renderOutlookReminderHint(sourceType: CalendarEventType): React.ReactNode {
    if (form.provider !== 'outlook' || !isReminderSourceEnabled(form.reminders, sourceType)) {
      return null;
    }
    return (
      <p className="text-sm text-muted-foreground">
        Outlook n’accepte qu’un seul rappel par événement. Cliquez une autre durée pour la
        remplacer.
      </p>
    );
  }

  /** Reminder section for a source type, optionally scoped to one session-type group. */
  function renderSourceReminders(
    type: CalendarEventType,
    sessionGroupId?: string,
  ): React.ReactNode {
    if (!form.enabledTypes.includes(type)) {
      return null;
    }
    if (appMode === 'remote' && type === 'event') {
      return null;
    }

    function renderSourcePreset(minutes: number): React.ReactNode {
      return renderReminderPreset(type, minutes);
    }

    function handleSourceRemindersEnabled(checked: boolean): void {
      handleRemindersEnabled(type, checked);
    }

    const sourceEnabled = isReminderSourceEnabled(form.reminders, type);
    const groupsToShow = sessionGroupId
      ? reminderSessionGroups.filter((g) => g.id === sessionGroupId)
      : reminderSessionGroups;

    function renderReminderContent(): React.ReactNode {
      if (type !== 'session') {
        return (
          <div className="flex flex-wrap gap-2">
            {CALENDAR_SYNC_REMINDER_PRESETS.map(renderSourcePreset)}
          </div>
        );
      }
      if (sessionGroupId) {
        const group = groupsToShow[0];
        if (!group) {
          return null;
        }
        if (group.types.length === 1) {
          return (
            <div className="flex flex-wrap gap-2">
              {CALENDAR_SYNC_REMINDER_PRESETS.map((m) =>
                renderReminderPreset('session', m, group.types[0]!.type),
              )}
            </div>
          );
        }
        return <div className="space-y-3">{group.types.map(renderSessionTypeReminderRow)}</div>;
      }
      return <div className="space-y-4">{groupsToShow.map(renderSessionReminderGroup)}</div>;
    }

    return (
      <div className="space-y-3 border-t border-border pt-4">
        <h4 className="text-sm font-semibold">Rappels</h4>
        <Checkbox
          label="Ajouter des rappels aux événements futurs"
          checked={sourceEnabled}
          onCheckedChange={handleSourceRemindersEnabled}
        />
        {renderOutlookReminderHint(type)}
        {sourceEnabled ? renderReminderContent() : null}
      </div>
    );
  }

  /** Full-width toggle card for one sync source type. */
  function renderTypeCard(item: CalendarLegendItem): React.ReactNode {
    if (appMode === 'remote' && item.type === 'event') {
      return null;
    }
    const config = EVENT_TYPE_CONFIG[item.type];
    const TypeIcon = resolveLucideIcon(config.fallbackIconName);
    const enabled = form.enabledTypes.includes(item.type);

    function handleToggle(): void {
      setForm((current) => toggleSyncType(current, item.type));
    }

    return (
      <button
        key={item.type}
        type="button"
        onClick={handleToggle}
        className={[
          'w-full flex items-center gap-4 rounded-xl px-5 py-4 border-2 transition-all text-left',
          enabled
            ? `${config.bgClass} ${config.borderClass} shadow-sm`
            : 'bg-card border-border hover:border-primary/40 hover:bg-primary/5',
        ].join(' ')}
      >
        <div
          className={['p-2.5 rounded-xl shrink-0', config.bgClass].join(' ')}
          style={{ color: config.color }}
        >
          <TypeIcon className="h-5 w-5" />
        </div>
        <div className="flex flex-col gap-0.5 flex-1">
          <span
            className={['text-base font-bold', enabled ? config.textClass : 'text-foreground'].join(
              ' ',
            )}
          >
            {item.label}
          </span>
          <span className="text-xs text-muted-foreground">
            {enabled ? 'Inclus dans la synchronisation' : 'Exclu de la synchronisation'}
          </span>
        </div>
        <div
          className={[
            'w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors',
            enabled ? 'border-current' : 'border-muted-foreground',
          ].join(' ')}
          style={enabled ? { color: config.color } : undefined}
        >
          {enabled ? <Check className="h-3 w-3" /> : null}
        </div>
      </button>
    );
  }

  function renderSessionListFilters(): React.ReactNode {
    if (appMode === 'remote') {
      const typesForStatus =
        form.sessionRemoteFilters.types.length > 0
          ? form.sessionRemoteFilters.types
          : sessionTypeOptions.map((item) => item.type);
      return (
        <RemoteSessionFilters
          activeTab="all"
          filters={{ ...form.sessionRemoteFilters, types: typesForStatus }}
          allowedFilterIds={[...ALL_SESSION_REMOTE_FILTER_GROUP_IDS]}
          visibleFilterIds={[...ALL_SESSION_REMOTE_FILTER_GROUP_IDS]}
          onToggleStatus={handleRemoteStatusToggle}
          onSetDateRange={handleRemoteDateRange}
          onSetCustomDateRange={handleRemoteCustomDates}
          onSetSearch={handleRemoteSearch}
          onSetTutorFilter={handleRemoteTutor}
          onSetStudentFilter={handleRemoteStudent}
          onSetManagedByFilter={handleRemoteManagedBy}
          onSetProgramFilter={handleRemoteProgram}
          onSetSchoolYearFilter={handleRemoteSchoolYear}
          onSetRecurrenceFilter={handleRemoteRecurrence}
        />
      );
    }
    return (
      <InPersonSessionFilters
        filters={form.sessionFilters}
        allowedFilterIds={SESSION_LIST_FILTER_IDS}
        visibleFilterIds={SESSION_LIST_FILTER_IDS}
        activeTab="all"
        onToggleType={handleInPersonTypeToggle}
        onToggleStatus={handleInPersonStatusToggle}
        onSetDateRange={handleInPersonDateRange}
        onSetCustomDateRange={handleInPersonCustomDates}
        onSetEstablishment={handleInPersonEstablishment}
        onSetSubject={handleInPersonSubject}
        onSetLevel={handleInPersonLevel}
        onSetSearch={handleInPersonSearch}
        onSetRecurrenceType={handleInPersonRecurrence}
        onSetAnimatorFilter={handleInPersonAnimator}
      />
    );
  }

  /** True when the host page owns this type, or the user opened the card. */
  function isSourceExpanded(type: CalendarEventType): boolean {
    if (expandedSources[type] !== undefined) {
      return expandedSources[type] === true;
    }
    return Boolean(lockedTypes?.includes(type));
  }

  /** Open the card and check it; close leaves the checkbox as the user left it. */
  function handleSourceExpand(type: CalendarEventType): void {
    const nextOpen = !isSourceExpanded(type);
    setExpandedSources((current) => ({
      ...current,
      [type]: nextOpen,
    }));
    if (nextOpen) {
      setForm((current) =>
        current.enabledTypes.includes(type) ? current : toggleSyncType(current, type),
      );
    }
  }

  /** Hide a source when the host page locked another type, or events in remote. */
  function isSourceHidden(type: CalendarEventType): boolean {
    if (form.lockedTypes?.length && !form.lockedTypes.includes(type)) {
      return true;
    }
    return appMode === 'remote' && type === 'event';
  }

  /** Edit-mode source accordion: checkbox includes the type, body is the filters. */
  function renderEditSourceCard(
    type: CalendarEventType,
    title: string,
    body: React.ReactNode,
  ): React.ReactNode {
    if (isSourceHidden(type)) {
      return null;
    }
    const enabled = form.enabledTypes.includes(type);
    const expanded = isSourceExpanded(type);
    const config = EVENT_TYPE_CONFIG[type];
    const TypeIcon = resolveLucideIcon(config.fallbackIconName);

    function handleToggle(): void {
      setForm((current) => toggleSyncType(current, type));
    }

    function handleExpand(): void {
      handleSourceExpand(type);
    }

    return (
      <div
        key={type}
        className={[
          'rounded-2xl overflow-hidden transition-all duration-200',
          enabled
            ? 'ring-2 ring-primary shadow-md'
            : 'ring-1 ring-border shadow-sm hover:ring-primary/50 hover:shadow-md',
        ].join(' ')}
      >
        <div
          className={[
            'flex w-full items-center gap-3 px-5 py-4 transition-colors',
            expanded ? 'bg-primary/10' : 'bg-card hover:bg-primary/5',
          ].join(' ')}
        >
          <button
            type="button"
            onClick={handleExpand}
            aria-expanded={expanded}
            className="flex min-w-0 flex-1 items-center justify-between text-left"
          >
            <div className="flex items-center gap-4">
              <div className={['p-2.5 rounded-xl', config.bgClass].join(' ')}>
                <TypeIcon className="h-5 w-5" style={{ color: config.color }} />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-base font-bold text-foreground">{title}</span>
                <span className="text-xs text-muted-foreground">
                  {enabled ? 'Inclus dans la synchronisation' : 'Exclu de la synchronisation'}
                </span>
              </div>
            </div>
            <ChevronDown
              className={[
                'h-5 w-5 text-primary transition-transform duration-200',
                expanded ? 'rotate-180' : '',
              ].join(' ')}
            />
          </button>
          <Checkbox checked={enabled} onCheckedChange={handleToggle} label="" />
        </div>
        {expanded ? (
          <div className="bg-background px-5 pb-6 pt-5 space-y-5 border-t-2 border-primary/20">
            {body}
          </div>
        ) : null}
      </div>
    );
  }

  /** Edit-mode session kinds as first-level accordions (no parent Séances card). */
  function renderSessionSection(): React.ReactNode {
    return sessionTypeGroupsForMode(appMode).map(renderGroupAccordion);
  }

  /** Edit-mode events accordion (in-person only). */
  function renderEventSection(): React.ReactNode {
    return renderEditSourceCard(
      'event',
      'Événements',
      <div className="space-y-4">
        {renderParticipateOnlyFilter()}
        <EventFiltersContent
          filters={form.eventFilters}
          allowedFilterIds={EVENT_FILTER_IDS}
          visibleFilterIds={EVENT_FILTER_IDS}
          onFiltersChange={handleEventFiltersChange}
        />
        {renderSourceReminders('event')}
      </div>,
    );
  }

  function handleInPersonTypeToggle(type: string): void {
    setForm((current) => toggleSessionFilterValue(current, 'types', type));
  }

  function handleInPersonStatusToggle(status: string): void {
    setForm((current) => toggleSessionFilterValue(current, 'statuses', status));
  }

  function handleInPersonDateRange(range: CalendarSyncState['sessionFilters']['dateRange']): void {
    setForm((current) => patchSessionFilters(current, { dateRange: range }));
  }

  function handleInPersonCustomDates(from: Date | undefined, to: Date | undefined): void {
    setForm((current) => patchSessionFilters(current, { customDateFrom: from, customDateTo: to }));
  }

  function handleInPersonEstablishment(id: string | undefined): void {
    setForm((current) => patchSessionFilters(current, { establishmentId: id }));
  }

  function handleInPersonSubject(id: string | undefined): void {
    setForm((current) => patchSessionFilters(current, { subjectId: id }));
  }

  function handleInPersonLevel(id: string | undefined): void {
    setForm((current) => patchSessionFilters(current, { levelId: id }));
  }

  function handleInPersonSearch(value: string | undefined): void {
    setForm((current) => patchSessionFilters(current, { search: value }));
  }

  function handleInPersonRecurrence(type: string | undefined): void {
    setForm((current) => patchSessionFilters(current, { recurrenceType: type }));
  }

  function handleInPersonAnimator(id: string | undefined): void {
    setForm((current) => patchSessionFilters(current, { animatorUserId: id }));
  }

  function handleRemoteStatusToggle(status: string): void {
    setForm((current) => toggleRemoteSessionStatus(current, status));
  }

  function handleRemoteDateRange(
    range: CalendarSyncState['sessionRemoteFilters']['dateRange'],
  ): void {
    setForm((current) => patchSessionRemoteFilters(current, { dateRange: range }));
  }

  function handleRemoteCustomDates(from: Date | undefined, to: Date | undefined): void {
    setForm((current) =>
      patchSessionRemoteFilters(current, { customDateFrom: from, customDateTo: to }),
    );
  }

  function handleRemoteSearch(value: string | undefined): void {
    setForm((current) => patchSessionRemoteFilters(current, { search: value }));
  }

  function handleRemoteTutor(id: string | undefined): void {
    setForm((current) => patchSessionRemoteFilters(current, { tutorUserId: id }));
  }

  function handleRemoteStudent(id: string | undefined): void {
    setForm((current) => patchSessionRemoteFilters(current, { studentUserId: id }));
  }

  function handleRemoteManagedBy(value: ManagedByValue): void {
    setForm((current) => patchSessionRemoteFilters(current, { managedByIds: value }));
  }

  function handleRemoteProgram(id: string | undefined): void {
    setForm((current) => patchSessionRemoteFilters(current, { programId: id }));
  }

  function handleRemoteSchoolYear(id: string | undefined): void {
    setForm((current) => patchSessionRemoteFilters(current, { schoolYearId: id }));
  }

  function handleRemoteRecurrence(type: string | undefined): void {
    setForm((current) => patchSessionRemoteFilters(current, { recurrenceType: type }));
  }

  function handleEventFiltersChange(next: EventFilters): void {
    setForm((current) => ({ ...current, eventFilters: next }));
  }

  function handleHolidayTypeToggle(type: string): void {
    setForm((current) => toggleHolidayType(current, type));
  }

  function handleHolidayDateRange(range: HolidayFilters['dateRange']): void {
    setForm((current) => ({
      ...current,
      holidayFilters: { ...current.holidayFilters, dateRange: range },
    }));
  }

  function handleHolidayCustomDates(from: Date | undefined, to: Date | undefined): void {
    setForm((current) => ({
      ...current,
      holidayFilters: { ...current.holidayFilters, customDateFrom: from, customDateTo: to },
    }));
  }

  /** Persist the holidays vacation-zone filter. */
  function handleHolidayVacationZone(value: string): void {
    setForm((current) => setHolidayVacationZone(current, value));
  }

  function handlePostitThematic(value: string): void {
    setForm((current) => ({
      ...current,
      postitFilters: {
        ...current.postitFilters,
        thematic: (value || undefined) as PostItThematic | undefined,
      },
    }));
  }

  function handlePostitStatus(value: string): void {
    setForm((current) => ({
      ...current,
      postitFilters: {
        ...current.postitFilters,
        status: (value || undefined) as PostItStatus | undefined,
      },
    }));
  }

  function handlePostitSearch(event: React.ChangeEvent<HTMLInputElement>): void {
    const value = event.target.value;
    setForm((current) => ({
      ...current,
      postitFilters: { ...current.postitFilters, search: value || undefined },
    }));
  }

  function handlePostitPriority(checked: boolean): void {
    setForm((current) => ({
      ...current,
      postitFilters: { ...current.postitFilters, priority: checked || undefined },
    }));
  }

  /** Copy the dashboard deadline period onto post-it prefs. */
  function handlePostitDateRange(value: string): void {
    setForm((current) => ({
      ...current,
      postitFilters: {
        ...current.postitFilters,
        dateRange: (value || 'all') as NonNullable<PostItFilters['dateRange']>,
      },
    }));
  }

  /** Copy the dashboard deadline start onto post-it prefs. */
  function handlePostitDateFrom(event: React.ChangeEvent<HTMLInputElement>): void {
    const value = event.target.value;
    setForm((current) => ({
      ...current,
      postitFilters: { ...current.postitFilters, dateFrom: value || undefined },
    }));
  }

  /** Copy the dashboard deadline end onto post-it prefs. */
  function handlePostitDateTo(event: React.ChangeEvent<HTMLInputElement>): void {
    const value = event.target.value;
    setForm((current) => ({
      ...current,
      postitFilters: { ...current.postitFilters, dateTo: value || undefined },
    }));
  }

  const thematicOptions = [
    { value: '', label: 'Toutes les thématiques' },
    ...THEMATICS.map((item) => ({ value: item.value, label: item.label })),
  ];
  const statusOptions = [
    { value: '', label: 'Tous les statuts' },
    ...Object.entries(POST_IT_STATUS_CONFIG).map(([value, config]) => ({
      value,
      label: config.label,
    })),
  ];

  const jobRunning = isCalendarSyncJobInProgress(job?.status);

  /** Progress line while the export job is pending, running, or slow. */
  function renderJobProgress(): React.ReactNode {
    if (!job || !jobRunning) {
      return null;
    }
    const count = `${job.processedItems}/${job.totalItems || '…'} éléments synchronisés`;
    if (job.status === 'slow') {
      return (
        <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          {getErrorMessage('calendarSyncSlow')} {count} {SYNC_SLOW_HINT}
        </p>
      );
    }
    return <p className="text-sm text-muted-foreground">{count}</p>;
  }

  /** Post-it bags: thematic / status / search / priority / deadline period. */
  function renderPostitSection(): React.ReactNode {
    return renderEditSourceCard(
      'postit',
      'Post-its',
      <div className="space-y-4">
        <div className="grid gap-3 md:grid-cols-2">
          <Select
            label="Thématique"
            value={form.postitFilters.thematic ?? ''}
            onChange={handlePostitThematic}
            options={thematicOptions}
          />
          <Select
            label="Statut"
            value={form.postitFilters.status ?? ''}
            onChange={handlePostitStatus}
            options={statusOptions}
          />
          <Select
            label="Période"
            value={form.postitFilters.dateRange ?? 'all'}
            onChange={handlePostitDateRange}
            options={[...DATE_RANGE_PERIOD_OPTIONS]}
          />
          <Input
            label="Recherche"
            value={form.postitFilters.search ?? ''}
            onChange={handlePostitSearch}
          />
          <Input
            label="Date de début"
            type="date"
            value={form.postitFilters.dateFrom ?? ''}
            onChange={handlePostitDateFrom}
          />
          <Input
            label="Date de fin"
            type="date"
            value={form.postitFilters.dateTo ?? ''}
            onChange={handlePostitDateTo}
          />
          <Checkbox
            label="Prioritaires uniquement"
            checked={form.postitFilters.priority === true}
            onCheckedChange={handlePostitPriority}
          />
        </div>
        {renderSourceReminders('postit')}
      </div>,
    );
  }

  /** Holiday bags — same groups as the holidays table (including zone). */
  function renderHolidaySection(): React.ReactNode {
    return renderEditSourceCard(
      'holiday',
      'Vacances',
      <div className="space-y-4">
        <HolidayFiltersContent
          filters={form.holidayFilters}
          allowedFilterIds={HOLIDAY_FILTER_IDS}
          visibleFilterIds={HOLIDAY_FILTER_IDS}
          onToggleType={handleHolidayTypeToggle}
          onSetDateRange={handleHolidayDateRange}
          onSetCustomDateRange={handleHolidayCustomDates}
          onSetVacationZone={handleHolidayVacationZone}
        />
        {renderSourceReminders('holiday')}
      </div>,
    );
  }

  const modalTitle =
    waitingForConnections || !isWizardMode
      ? 'Synchroniser le calendrier'
      : 'Configuration de la synchronisation';
  const hideModalActions = waitingForConnections || isWizardMode || currentStep === 'progress';

  /** True when export would be rejected (no provider, no type, or empty session groups). */
  function isSyncActionDisabled(): boolean {
    return (
      submitting ||
      jobRunning ||
      !form.provider ||
      form.enabledTypes.length === 0 ||
      (form.enabledTypes.includes('session') && selectedSessionTypes.length === 0)
    );
  }

  function renderDisconnectBody(): React.ReactNode {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Le calendrier ZUPdeCO déjà exporté reste dans votre agenda externe, sauf si vous le
          retirez.
        </p>
        <RadioGroup
          name="calendar-sync-disconnect"
          layout="cards"
          options={DISCONNECT_OPTIONS}
          value={disconnectMode}
          onValueChange={handleDisconnectModeChange}
        />
      </div>
    );
  }

  return (
    <>
      <InfoModal
        open={open}
        onClose={handleClose}
        icon={Calendar}
        title={modalTitle}
        subtitle="Synchroniser votre planning avec votre agenda externe"
        size="3xl"
        reserveFooter={waitingForConnections}
        actions={
          hideModalActions
            ? []
            : [
                { id: 'cancel', label: 'Fermer', onClick: handleClose, preset: 'modal-cancel' },
                {
                  id: 'sync',
                  label: jobRunning ? 'Synchro en cours…' : 'Synchroniser',
                  onClick: handleSync,
                  preset: 'modal-confirm',
                  icon: RefreshCw,
                  loading: submitting || jobRunning,
                  disabled: isSyncActionDisabled(),
                },
              ]
        }
      >
        {renderModalBody()}
      </InfoModal>
      <InfoModal
        open={disconnectOpen}
        onClose={handleCloseDisconnect}
        icon={Unlink}
        title="Déconnecter l’agenda"
        subtitle="Choisissez si le calendrier ZUPdeCO doit être supprimé, ou seulement arrêté."
        size="md"
        actions={[
          {
            id: 'cancel',
            label: 'Annuler',
            onClick: handleCloseDisconnect,
            preset: 'modal-cancel',
            disabled: disconnecting,
          },
          {
            id: 'confirm',
            label: disconnecting ? 'Déconnexion…' : 'Confirmer',
            onClick: handleConfirmDisconnect,
            preset: 'modal-danger',
            icon: Unlink,
            loading: disconnecting,
            disabled: disconnecting,
          },
        ]}
      >
        {renderDisconnectBody()}
      </InfoModal>
    </>
  );
};
